import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ResizableTable from '../common/ResizableTable';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import SystemNotificationDetailModal from './SystemNotificationDetailModal';
import { formatDate } from '../../utils/formatUtils';
import './NotificationsPage.css';

const RECORDS_PER_PAGE = 25;

function cell(value, displayValue) {
  const v = value ?? '';
  const d = displayValue ?? v;
  return {
    value: v,
    displayValue: d === '' ? '—' : String(d),
  };
}

function formatRelativeTime(timestamp) {
  if (!timestamp) return 'Just now';

  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(timestamp);
  } catch {
    return 'Just now';
  }
}

function dispatchNotificationsRefresh() {
  window.dispatchEvent(new CustomEvent('ibs-refresh-system-notifications'));
}

function getNotificationId(notification) {
  return notification?.id || notification?.notification_id;
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const notificationApi = useMemo(() => new DataApi('notifications'), []);

  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / RECORDS_PER_PAGE));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const offset = (page - 1) * RECORDS_PER_PAGE;
      const unreadQuery = unreadOnly ? 'true' : 'false';
      const searchQuery = debouncedSearch
        ? `&search=${encodeURIComponent(debouncedSearch)}`
        : '';
      const [listResponse, countResponse] = await Promise.all([
        notificationApi.fetchAll(
          `limit=${RECORDS_PER_PAGE}&offset=${offset}&unreadOnly=${unreadQuery}${searchQuery}`
        ),
        notificationApi.get(
          `/count?unreadOnly=${unreadQuery}${searchQuery}`
        ),
      ]);

      const list =
        listResponse?.data && Array.isArray(listResponse.data)
          ? listResponse.data
          : [];
      const count =
        countResponse?.data?.count !== undefined
          ? Number(countResponse.data.count)
          : list.length;

      setNotifications(list);
      setTotal(Number.isFinite(count) ? count : list.length);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load notifications');
      setNotifications([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [notificationApi, page, unreadOnly, debouncedSearch]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    setPage(1);
  }, [unreadOnly, debouncedSearch]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const notificationById = useMemo(() => {
    const map = new Map();
    notifications.forEach(notification => {
      const id = getNotificationId(notification);
      if (id) map.set(String(id), notification);
    });
    return map;
  }, [notifications]);

  const tableData = useMemo(
    () =>
      notifications.map(notification => {
        const id = getNotificationId(notification);
        const isUnread = !notification.is_read;
        const typeLabel = notification.notification_type
          ? String(notification.notification_type).replace(/_/g, ' ')
          : '—';
        const relatedLabel = notification.related_object
          ? notification.related_object.charAt(0).toUpperCase() +
            notification.related_object.slice(1)
          : '—';
        const messagePreview = notification.message
          ? notification.message.length > 100
            ? `${notification.message.substring(0, 100)}…`
            : notification.message
          : '—';

        return {
          id: cell(id),
          title: cell(notification.title, notification.title || 'Notification'),
          message: cell(notification.message, messagePreview),
          type: cell(notification.notification_type, typeLabel),
          related: cell(notification.related_object, relatedLabel),
          status: cell(isUnread ? 'unread' : 'read', isUnread ? 'Unread' : 'Read'),
          received: cell(
            notification.createddate,
            formatRelativeTime(notification.createddate)
          ),
        };
      }),
    [notifications]
  );

  const columns = useMemo(
    () => [
      { field: 'title', label: 'Title', sortable: true, width: '220px' },
      { field: 'message', label: 'Message', sortable: true, width: '280px' },
      { field: 'type', label: 'Type', sortable: true, width: '140px' },
      { field: 'related', label: 'Related', sortable: true, width: '120px' },
      { field: 'status', label: 'Status', sortable: true, width: '100px' },
      { field: 'received', label: 'Received', sortable: true, width: '130px' },
    ],
    []
  );

  const markAsRead = async notificationId => {
    if (!notificationId) return;
    try {
      await notificationApi.put(`/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(notif =>
          getNotificationId(notif) === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );
      dispatchNotificationsRefresh();
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const deleteNotification = async notificationId => {
    if (!notificationId) return;
    try {
      const response = await notificationApi.delete(notificationId);
      if (response?.data?.success !== false) {
        setNotifications(prev =>
          prev.filter(notif => getNotificationId(notif) !== notificationId)
        );
        setTotal(prev => Math.max(0, prev - 1));
        dispatchNotificationsRefresh();
        if (
          selectedNotification &&
          getNotificationId(selectedNotification) === notificationId
        ) {
          setDetailModalOpen(false);
          setSelectedNotification(null);
        }
      }
    } catch (e) {
      console.error('Error deleting notification:', e);
    }
  };

  const openNotificationDetail = async notification => {
    if (!notification) return;
    const notificationId = getNotificationId(notification);
    setSelectedNotification(notification);
    setDetailModalOpen(true);

    if (!notification.is_read && notificationId) {
      await markAsRead(notificationId);
      setSelectedNotification(prev => (prev ? { ...prev, is_read: true } : prev));
    }

    if (notificationId) {
      setLoadingDetail(true);
      try {
        const response = await notificationApi.fetchById(notificationId);
        if (response?.data?.id) {
          setSelectedNotification(response.data);
        }
      } catch (e) {
        console.warn('Could not load notification detail:', e);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleOpenRecord = notification => {
    if (notification?.related_object && notification?.parentid) {
      setDetailModalOpen(false);
      navigate(`/view/${notification.related_object}/${notification.parentid}`);
    }
  };

  const handleDeleteFromModal = async notificationId => {
    if (!notificationId || !window.confirm('Delete this notification?')) return;
    setDeletingNotification(true);
    try {
      await deleteNotification(notificationId);
    } finally {
      setDeletingNotification(false);
    }
  };

  const markAllAsRead = async () => {
    setMarkingAllRead(true);
    setSuccess('');
    try {
      await notificationApi.put('/read-all');
      setSuccess('All notifications marked as read.');
      await loadNotifications();
      dispatchNotificationsRefresh();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const deleteAll = async () => {
    setDeletingAll(true);
    setError('');
    try {
      const response = await notificationApi.delete('delete-all');
      if (response?.data?.success !== false) {
        setNotifications([]);
        setTotal(0);
        setShowDeleteAllConfirm(false);
        setDetailModalOpen(false);
        setSelectedNotification(null);
        setSuccess('All notifications deleted.');
        dispatchNotificationsRefresh();
      } else {
        setError(response?.data?.message || 'Failed to delete notifications');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete notifications');
    } finally {
      setDeletingAll(false);
    }
  };

  const actionsRenderer = record => {
    const id = record?.id?.value;
    const notification = id ? notificationById.get(String(id)) : null;
    if (!notification) return null;

    const isUnread = !notification.is_read;

    return (
      <div className='notifications-row-actions d-flex gap-2 flex-nowrap'>
        {isUnread && (
          <Button
            size='sm'
            variant='outline-primary'
            onClick={e => {
              e.stopPropagation();
              void markAsRead(id);
            }}
          >
            Mark read
          </Button>
        )}
        <Button
          size='sm'
          variant='outline-danger'
          onClick={e => {
            e.stopPropagation();
            if (window.confirm('Are you sure you want to delete this notification?')) {
              void deleteNotification(id);
            }
          }}
        >
          Delete
        </Button>
      </div>
    );
  };

  const handleRowClick = record => {
    const id = record?.id?.value;
    const notification = id ? notificationById.get(String(id)) : null;
    if (notification) {
      void openNotificationDetail(notification);
    }
  };

  return (
    <Container fluid className='notifications-page py-4'>
      <Row className='mb-3 align-items-center'>
        <Col>
          <h4 className='mb-1 d-flex align-items-center gap-2'>
            <FaBell style={{ color: '#0176d3' }} />
            Notifications
          </h4>
          <p className='text-muted mb-0 small'>
            View and manage all your notifications
          </p>
        </Col>
        <Col xs='auto' className='d-flex flex-wrap gap-2 justify-content-end'>
          <Button
            variant='outline-primary'
            size='sm'
            disabled={markingAllRead || total === 0}
            onClick={() => void markAllAsRead()}
          >
            {markingAllRead ? 'Marking…' : 'Mark all as read'}
          </Button>
          <Button
            variant='outline-danger'
            size='sm'
            disabled={total === 0}
            onClick={() => setShowDeleteAllConfirm(true)}
          >
            Delete all
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant='danger' dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant='success' dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card className='notifications-page-card border-0 shadow-sm'>
        <Card.Body className='p-3'>
          <div className='notifications-page-toolbar mb-3'>
            <Form.Select
              size='sm'
              value={unreadOnly ? 'unread' : 'all'}
              onChange={e => setUnreadOnly(e.target.value === 'unread')}
              style={{ maxWidth: '180px' }}
            >
              <option value='all'>All notifications</option>
              <option value='unread'>Unread only</option>
            </Form.Select>
          </div>

          <ResizableTable
            className='notifications-table'
            title=''
            data={tableData}
            columns={columns}
            loading={loading}
            searchTerm={search}
            onSearchChange={value => {
              setSearch(value);
              setPage(1);
            }}
            currentPage={page}
            totalRecords={total}
            recordsPerPage={RECORDS_PER_PAGE}
            onPageChange={setPage}
            showSerialNumber
            showActions
            actionsRenderer={actionsRenderer}
            showNewButton={false}
            showPagination={total > RECORDS_PER_PAGE}
            searchPlaceholder='Search notifications…'
            emptyMessage='No notifications found.'
            onRefresh={loadNotifications}
            onRowClick={handleRowClick}
            fitViewportHeight
          />
        </Card.Body>
      </Card>

      <SystemNotificationDetailModal
        show={detailModalOpen}
        onHide={() => {
          setDetailModalOpen(false);
          setSelectedNotification(null);
        }}
        notification={selectedNotification}
        loading={loadingDetail}
        onOpenRecord={handleOpenRecord}
        onDelete={handleDeleteFromModal}
        deleting={deletingNotification}
      />

      <DeleteConfirmationModal
        show={showDeleteAllConfirm}
        onHide={() => {
          if (!deletingAll) {
            setShowDeleteAllConfirm(false);
          }
        }}
        onConfirm={deleteAll}
        title='Delete all notifications'
        message='Remove every notification from your list? This cannot be undone.'
        confirmText='Delete all'
        isDeleting={deletingAll}
      />
    </Container>
  );
};

export default NotificationsPage;
